import type { WhaleState } from './types';
import { civilFromEpochDay, dayOfYear, daysInMonth, epochDayFromCivil, spanIn } from './calendar';
import { YEAR_DAYS, type PopulationDraws } from './population';

/**
 * The humpback whales that winter in Hawaii (M101): the Hawaii population
 * (NOAA's Hawaii stock, its distinct population segment), counted whole by
 * year class since birth, which Maui Nui's waters are a part of the winter
 * grounds of (`GDD.md` section 7). The day's step ages them on the first of
 * January; they die, each class at its stage's rate; and over the winter's
 * peak the adult females calve. Each day a share of the population is in
 * Hawaii's waters, by NOAA's model of the whales there month by month; the
 * rest are on their feeding grounds off Alaska and British Columbia or
 * migrating, by NOAA's estimates of where each feeds (M106). Its draws are the caller's, as the honu's are (`turtles.ts`):
 * binomial deaths and Poisson calves on its own stream in the game, expected
 * values in the balance (`scripts/humpback.ts`).
 *
 * **The balance**: the population held at NOAA's estimate, as every
 * population in the game is held at today's numbers until its trend is
 * coded; the young's survival from six months to maturity, which no source
 * read measures, the one fitted number, set so the population replaces
 * itself.
 *
 * **Maui Nui's share** of the whales in Hawaii's waters no source read gives
 * as a number ("The winter range includes the main islands of the Hawaiian
 * archipelago, with the greatest concentration along the west side of Maui",
 * Muto et al. 2021, p. 229, in words); until one is read, the card gives
 * Hawaii's.
 */

/** A whale's life, each figure with its source. */
export interface WhaleLife {
  id: 'humpback';
  /** The population the balance holds, its year's mean. */
  population: number;
  /** Whales in Hawaii's waters each month, January to December, the model's average, and the peak abundance the population is reckoned by. */
  inHawaiiByMonth: readonly number[];
  inHawaiiPeak: number;
  /** Survival: the calves' to six months, and the adults' a year. */
  calfSixMonthSurvival: number;
  adultSurvival: number;
  /** Calves each adult female has with her on the feeding grounds a year, the six months' survivors (she bears that over the calves' six-month survival), and the population's share female. */
  calvesPerAdultFemale: number;
  femaleShare: number;
  /** The age a whale is an adult from, years. */
  matureYears: number;
  /** The oldest class, years: its whales die at the year's turn. */
  oldestYears: number;
  /** The days of the year calves are born over, [month, day] to [month, day], both in it. */
  calving: { from: readonly [number, number]; to: readonly [number, number] };
  /** Where its whales feed: each feeding ground's share of the population, NOAA's estimates. */
  feeding: readonly { ground: string; share: number }[];
}

/**
 * The humpback whale, *Megaptera novaeangliae* (koholā), of the Hawaii
 * population, each figure with its source as read (M101).
 */
export const HUMPBACK: WhaleLife = {
  id: 'humpback',
  /**
   * "The resulting estimate of abundance was 11,278 (CV = 0.56, 95% CI
   * 4,049-31,412) (Becker et al. 2022), which is considered the best current
   * estimate of abundance for Hawaiʻi and for the stock as a whole" (Young et
   * al. 2023, p. 241), the whales in Hawaii's waters at the 2020 peak by
   * NOAA's model of its ship surveys. "It is unknown if this population is
   * currently increasing" (ibid., p. 242): held level (open: its rise of about
   * 7% a year to 2013 and its fall of 34% to 2021, Cheeseman et al. 2024).
   */
  population: 11_278,
  /**
   * "Monthly average (2017−2020) model-predicted estimates of humpback whale
   * abundance ... within the Hawaiian Islands EEZ" (Becker et al. 2022, Table
   * 9, p. 16): January 1,918, February 8,572, March 9,250, April 3,401, May
   * 404, "June-October 8*" ("*Average for the 5 months."), November 485,
   * December 4,465, each month's at its middle day and the days between by a
   * straight line. January's below December's is the model's (open). The
   * same model's 2020 peak, 11,278, "may underrepresent the full abundance of
   * whales that overwinter in the region because individual whales may not
   * have very long residence times" (ibid., p. 17): each month's is taken as
   * its share of the population, the whales in Hawaii's waters the
   * population's that share (a reading: the population the peak). So the
   * whales in Hawaii's waters are never the whole population (9,537 at most,
   * on 16 March, in the balance's year): the months' are 2017-2020's, each
   * over its whole month, and the peak is "the peak abundance of humpback
   * whales in the Hawaiian Islands EEZ during 2020" (ibid., p. 17), one year's
   * at its peak days; each month is shown as the table gives it.
   */
  inHawaiiByMonth: [1918, 8572, 9250, 3401, 404, 8, 8, 8, 8, 8, 485, 4465],
  inHawaiiPeak: 11_278,
  /** "Gabriele (2001) estimated mortality of juveniles at 6 months of age to be 0.182 (95% CI: 0.023-0.518)" (Bettridge et al. 2015, p. 10), "Using associations of calves with identified mothers on North Pacific breeding and feeding grounds". */
  calfSixMonthSurvival: 1 - 0.182,
  /** "Annual adult mortality rates have been estimated to be ... 0.037 (95% CI 0.022-0.056) (Mizroch et al. 2004) in the North Pacific Hawaiian Islands populations" (Bettridge et al. 2015, pp. 9-10). */
  adultSurvival: 1 - 0.037,
  /**
   * In Glacier Bay and Icy Strait, Southeast Alaska, where whales of the
   * Hawaii population feed, its whales "sighted June-August" (Schakner et al.
   * 2022, p. 6): "Prior to the PMH [the 2014-2016 Pacific marine heatwave],
   * calf production averaged one calf per three females (0.27 calves per adult
   * female)" (ibid., from Gabriele et al. 2022), the figure taken (its
   * words' one in three is 0.33). A summer's count on the feeding grounds: the
   * calves that lived through the migration, Gabriele 2001's six months
   * (`calfSixMonthSurvival`), so each adult female bears 0.27 / 0.818 = 0.330
   * a year in Hawaii (M101's review). "Estimated mean calving rates are between
   * 0.38 and 0.50 calves per mature female per year (Clapham and Mayo 1990;
   * Straley et al. 1994; Steiger and Calambokidis 2000)" (Bettridge et al.
   * 2015, p. 9): one of the three, Straley et al. 1994, is this population's
   * in Alaskan waters, its own figure unread; the range is passed over for the
   * same program's later record, 0.27.
   */
  calvesPerAdultFemale: 0.27,
  /** "Although the overall sex ratio is one male to one female, within the breeding grounds the observed sex ratio is approximately two males to one female" (NOAA's sanctuary condition report, 2010). */
  femaleShare: 0.5,
  /**
   * "Sexual maturity of humpback whales in the Northern Hemisphere occurs at
   * approximately 5-11 years of age, and appears to vary both within and among
   * populations" (Bettridge et al. 2015, p. 9): the middle of that range,
   * assumed, every whale an adult from 8 (open: this population's own age at
   * first calving, Gabriele et al. 2007, unread).
   */
  matureYears: 8,
  /** "Lifespan ... About 80 to 90 years" (NOAA Fisheries' humpback page, no source given): the classes run to 90, and a whale of the last dies at the year's turn, none living to 91 (M101's review: gathered, the last held 2.9% of the whales, 116 years old on average). */
  oldestYears: 90,
  /**
   * "Based on the functional plot for Julian date (Figure 2), peak numbers of
   * humpback whales are expected to occur within the Hawaiian Islands EEZ from
   * approximately February 19 through March 22" (Becker et al. 2022, p. 16): calves born over it (open: the season's births by day,
   * unread; "calves are born in tropical waters", Bettridge et al. 2015, p. 9).
   */
  calving: { from: [2, 19], to: [3, 22] },
  /**
   * "For winter-to-summer migration, an estimated 98% of the whales in
   * Hawaiʻi had a migratory destination to one of the three feeding areas in
   * Alaska and northern British Columbia, with the majority going to
   * Southeast Alaska/northern British Columbia. The remaining small
   * percentage of whales are estimated to migrate to Russia (0.3%) or
   * southern British Columbia/Washington (1.1%)" (Wade, Oleson and Young
   * 2021, NOAA Tech. Memo. NMFS-AFSC-430, p. 7): Wade 2021's model of
   * SPLASH's 2004-2006 photographs, made because "the raw photographic
   * matching rates do not account for differences in capture probabilities
   * between strata" (ibid., p. 6; the matches are the world map's,
   * `ui/migrants.ts`). Of its three, Southeast Alaska and northern British
   * Columbia's and the other two's together from NOAA's "Movement
   * probabilities from Wade (2021)" for Hawaiʻi (Young et al. 2023, Table 2,
   * p. 243): 0.809 to Southeast Alaska / Northern British Columbia and
   * southern British Columbia / Washington, "the summed estimates" (ibid.,
   * p. 242), less the 1.1%; 0.191 to "Russia, the Bering Sea / Aleutian
   * Islands, and the Gulf of Alaska", less the 0.3%. The three's 98.6% the
   * text gives as 98%. The Gulf of Alaska's apart from the Aleutians' and the
   * Bering Sea's is Wade 2021's own, unread (open).
   */
  feeding: [
    { ground: 'Southeast Alaska and northern British Columbia', share: 0.809 - 0.011 },
    { ground: 'the Gulf of Alaska, the Aleutian Islands and the Bering Sea', share: 0.191 - 0.003 },
    { ground: 'southern British Columbia and Washington', share: 0.011 },
    { ground: 'Russia', share: 0.003 },
  ],
};

/** What the balance sets (`data/whales/`): the young's survival a year from six months to maturity. */
export interface WhaleSupply {
  youngSurvival: number;
}

/** A year's months (a definition). */
const MONTHS = 12;
/** A calf's span, years: the six months the calves' survival is measured over (Gabriele 2001's "at 6 months of age"). */
const CALF_YEARS = 0.5;

/** The classes: 0 to `oldestYears`. */
export const whaleClasses = (life: WhaleLife): number => life.oldestYears + 1;

/** The day of a common year calves are born on, on average: the middle of the calving span (2001's). */
const birthDays = new Map<WhaleLife, number>();
function birthDay(life: WhaleLife): number {
  let day = birthDays.get(life);
  if (day === undefined) {
    const { first, days } = spanIn(life.calving, 2001);
    day = dayOfYear(first) + (days - 1) / 2;
    birthDays.set(life, day);
  }
  return day;
}

/** A class's whales' mean age in years on a day of the year: class 0 this year's calves. */
const ageOn = (life: WhaleLife, cls: number, doy: number): number => cls + (doy - birthDay(life)) / YEAR_DAYS;

export type WhaleStage = 'calf' | 'young' | 'adult';
/** A whale's stage by its age: a calf to six months (the calves' survival's span), young to `matureYears`, then an adult. */
export const stageOf = (life: WhaleLife, ageYears: number): WhaleStage => (ageYears < CALF_YEARS ? 'calf' : ageYears < life.matureYears ? 'young' : 'adult');

const dailyDeath = (survival: number, overYears: number): number => 1 - survival ** (1 / (overYears * YEAR_DAYS));

/** On the first of January, every class a year older, the oldest's whales dead of age; on any other day, nothing. */
function ageWhales(life: WhaleLife, w: WhaleState, epochDay: number): void {
  if (dayOfYear(epochDay) !== 1) return;
  const c = w.counts;
  for (let k = whaleClasses(life) - 1; k > 0; k--) c[k] = c[k - 1] as number;
  c[0] = 0;
}

/**
 * A day of the whales' lives, the classes aged (`ageWhales`): over the
 * calving span, the day's calves, the adult females' rate a year (the
 * feeding grounds' over the calves' six-month survival) spread evenly over
 * its days; then each class's deaths at its stage's rate (a calf's six-month
 * survival spread over its six months, the young's and the adults' over a
 * year), the day's calves among them, so a calf's days at the calves' rate
 * are 182.5 on average (M101's review: born after the deaths, 181.5).
 */
export function stepHumpback(life: WhaleLife, w: WhaleState, epochDay: number, supply: WhaleSupply, draws: PopulationDraws): void {
  ageWhales(life, w, epochDay);
  const doy = dayOfYear(epochDay);
  const { first, days } = spanIn(life.calving, civilFromEpochDay(epochDay).year);
  const day = epochDay - first;
  if (day >= 0 && day < days) {
    let adults = 0;
    for (let k = 0; k < w.counts.length; k++) if (stageOf(life, ageOn(life, k, doy)) === 'adult') adults += w.counts[k] as number;
    const born = life.calvesPerAdultFemale / life.calfSixMonthSurvival;
    w.counts[0] = (w.counts[0] as number) + draws.count((born * life.femaleShare * adults) / days);
  }
  const p = { calf: dailyDeath(life.calfSixMonthSurvival, CALF_YEARS), young: dailyDeath(supply.youngSurvival, 1), adult: dailyDeath(life.adultSurvival, 1) };
  for (let k = 0; k < w.counts.length; k++) {
    const n = w.counts[k] as number;
    if (n > 0) w.counts[k] = n - draws.share(n, p[stageOf(life, ageOn(life, k, doy))]);
  }
}

/** A whale's age in years on a day, its class's mean (M148). */
export const whaleAgeYears = (life: WhaleLife, cls: number, epochDay: number): number => ageOn(life, cls, dayOfYear(epochDay));

/**
 * One tagged humpback's day (M148), as its class's (`stepHumpback`'s rates): a year older on the first of January, dead
 * of its age past the oldest class as its class is, then dead by its stage's chance or not. `u` its own even draw.
 */
export function stepOneWhale(life: WhaleLife, supply: WhaleSupply, whale: { cls: number }, epochDay: number, u: () => number): 'old age' | 'natural' | null {
  const doy = dayOfYear(epochDay);
  if (doy === 1) {
    if (whale.cls >= whaleClasses(life) - 1) return 'old age';
    whale.cls += 1;
  }
  const p = { calf: dailyDeath(life.calfSixMonthSurvival, CALF_YEARS), young: dailyDeath(supply.youngSurvival, 1), adult: dailyDeath(life.adultSurvival, 1) };
  return u() < p[stageOf(life, ageOn(life, whale.cls, doy))] ? 'natural' : null;
}

/**
 * Whether a tagged whale is in Hawaiʻi's waters on a day (M148): its own fixed place in the population, `quantile` (an
 * even draw as it is tagged), below the day's share of the whales there (`inHawaiiShare`), so each whale comes and goes
 * once a season around its peak and the tagged are in Hawaiʻi as the population's share is.
 */
export const whaleInHawaii = (life: WhaleLife, quantile: number, epochDay: number): boolean => quantile < inHawaiiShare(life, epochDay);

/** Whales in Hawaii's waters a day of the year, of the model's peak: each month's at its middle day, a straight line between. */
export function inHawaiiShare(life: WhaleLife, epochDay: number): number {
  const { year } = civilFromEpochDay(epochDay);
  const mid = (m: number): number => {
    // A month's middle day as an epoch day, months before January and after December in the years beside.
    const y = year + Math.floor((m - 1) / MONTHS);
    const month = ((((m - 1) % MONTHS) + MONTHS) % MONTHS) + 1;
    return epochDayFromCivil(y, month, 1) + (daysInMonth(y, month) - 1) / 2;
  };
  const value = (m: number): number => life.inHawaiiByMonth[((((m - 1) % MONTHS) + MONTHS) % MONTHS)] as number;
  let m = civilFromEpochDay(epochDay).month;
  if (epochDay < mid(m)) m -= 1;
  const t = (epochDay - mid(m)) / (mid(m + 1) - mid(m));
  return (value(m) + t * (value(m + 1) - value(m))) / life.inHawaiiPeak;
}

/**
 * What the card reads on a day: the whole population, this year's calves,
 * the whales in Hawaii's waters, and the others by the feeding ground each
 * feeds on, there or on the way (M106: where each is on the way is open, its
 * sources refused here, and Becker's January below its December would set
 * whales leaving Hawaii in late December and the feeding grounds empty by
 * mid-January at the fastest trips recorded).
 */
export interface WhaleCounts {
  population: number;
  calves: number;
  inHawaii: number;
  away: readonly { ground: string; whales: number }[];
}
export function whaleCounts(life: WhaleLife, w: WhaleState, epochDay: number): WhaleCounts {
  const population = w.counts.reduce((a, b) => a + b, 0);
  const inHawaii = population * inHawaiiShare(life, epochDay);
  return { population, calves: w.counts[0] as number, inHawaii, away: life.feeding.map((f) => ({ ground: f.ground, whales: (population - inHawaii) * f.share })) };
}

export function emptyWhales(life: WhaleLife): WhaleState {
  return { counts: Array<number>(whaleClasses(life)).fill(0) };
}
